import { useNavigate, useSearch } from "@tanstack/react-router";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DeliveryForm, { type DeliveryFormData } from "./DeliveryForm";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";

export default function DeliveryCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Read pre-fill params from URL (from OrderView shortcut)
  const search = (useSearch({ strict: false }) || {}) as Record<string, string>;
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const prefillOrderId = search.orderId || urlParams?.get("orderId") || "";
  const prefillCustomerId = search.customerId || urlParams?.get("customerId") || "";
  const rawDetailIds = search.detailIds || urlParams?.get("detailIds") || "";
  const prefillDetailIds = rawDetailIds
    ? rawDetailIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined;

  const rawFqcBatchIds = search.fqcBatchIds || urlParams?.get("fqcBatchIds") || "";
  const prefillFqcBatchIds = rawFqcBatchIds
    ? rawFqcBatchIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined;

  const handleSubmit = async (data: DeliveryFormData) => {
    try {
      const { items, ...header } = data;
      await trpcClient.deliveries.create.mutate({
        customerId: header.customerId,
        orderId: header.orderId || undefined,
        salesUserId: header.salesUserId || undefined,
        deliveryDate: header.deliveryDate,
        deliveryType: header.deliveryType,
        deliveryFee: header.deliveryFee,
        address: header.address || undefined,
        transshipmentAddr: header.transshipmentAddr || undefined,
        receiverName: header.receiverName || undefined,
        receiverPhone: header.receiverPhone || undefined,
        status: header.status || "DRAFT",
        saveMode: (header as any).saveMode,
        driverName: header.driverName || undefined,
        vehicleNumber: header.vehicleNumber || undefined,
        notes: header.notes || undefined,
        totalAmount: header.totalAmount,
        items: items.map((it) => ({
          orderDetailId: it.orderDetailId || undefined,
          productId: it.productId || undefined,
          productName: it.productName,
          poNumber: it.poNumber || undefined,
          soNumber: it.soNumber || undefined,
          unit: it.unit,
          quantity: it.quantity,
          deliveredQty: it.deliveredQty,
          lossQty: it.lossQty,
          countedQty: it.countedQty,
          unitPrice: it.unitPrice,
          amount: it.amount,
          notes: it.notes || undefined,
          isServiceItem: Boolean(it.isServiceItem),
          serviceType: it.serviceType || undefined,
        })),
      });
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });

      navigate({ to: "/delivery", replace: true });
    } catch (err: any) {
      toast.error(err?.message || "Tạo phiếu giao hàng thất bại");
    }
  };

  const handleBack = () => {
    if (prefillOrderId) {
      navigate({ to: `/orders/${prefillOrderId}` as any });
    } else {
      navigate({ to: "/delivery" });
    }
  };

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Giao Hàng", to: "/delivery" },
        { label: "Tạo mới" },
      ]}
      title="Tạo Phiếu Giao Hàng"
      onBack={handleBack}
      overrideBack={Boolean(prefillOrderId)}
      showContextPanel={false}
    >
      <div className="w-full p-4 animate-in fade-in duration-300">
        <DeliveryForm
          defaultValues={{
            orderId: prefillOrderId,
            customerId: prefillCustomerId,
          }}
          filterDetailIds={prefillDetailIds}
          fqcBatchIds={prefillFqcBatchIds}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          submitLabel="Lưu"
        />
      </div>
    </StandardDetailLayout>
  );
}
